import type { ChangeEvent } from 'react';
import React, { useEffect, useRef } from 'react';
import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { DropDown, IconInfoCircular } from '@noths/polaris-client-ribbons-design-system';

import { DeliveryOptions } from '../DeliveryOptions/DeliveryOptions';
import { PhotoPersonalisationMessage } from '../PhotoPersonalisationMessage/PhotoPersonalisationMessage';
import {
  COUNTRY_SELECT_LABEL,
  DELIVERY_NOT_AVAILABLE_TO_COUNTRY_FIELD_ERROR,
  DELIVERY_NOT_AVAILABLE_TO_COUNTRY_MESSAGE,
  TIMEZONE_MESSAGE,
} from './copy';
import type { DeliveryBlockProps } from './types';
import * as styles from './DeliveryBlock.styles';

export const DeliveryBlock = ({
  arrivesBy,
  deliveryOptions,
  deliveryOptionsForCurrentZone,
  groupedCountries,
  hasAnyDeliveryZones,
  loading,
  onCountryChange,
  partnerName,
  photoUploadRequired,
  selectedCountry,
}: DeliveryBlockProps) => {
  const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const [countryCode, deliveryZone] = e.target.value.split('|') as [string, DeliveryZoneCode];

    onCountryChange({
      selectedCountry: countryCode,
      selectedZone: deliveryZone,
    });
  };

  const isDeliveryAvailableForCurrentZone = Boolean(deliveryOptionsForCurrentZone.length);
  const showTimezoneMessage = useRef(false);

  useEffect(() => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    showTimezoneMessage.current = userTimeZone !== 'Europe/London';
  }, []);

  const renderDeliveryOptions = () => {
    if (loading) {
      return <div>Loading delivery options...</div>;
    }

    if (!hasAnyDeliveryZones) {
      return null;
    }

    if (isDeliveryAvailableForCurrentZone) {
      return (
        <DeliveryOptions
          arrivesBy={arrivesBy}
          deliveryOptions={deliveryOptions}
          partnerName={partnerName}
        />
      );
    }

    return (
      <p css={styles.deliveryNotAvailableMessage}>{DELIVERY_NOT_AVAILABLE_TO_COUNTRY_MESSAGE}</p>
    );
  };

  return (
    <section css={styles.deliveryBlock}>
      <header>
        <h2>Delivery info</h2>
        <DropDown
          css={styles.countrySelect}
          disabled={loading}
          error={
            hasAnyDeliveryZones && !isDeliveryAvailableForCurrentZone
              ? DELIVERY_NOT_AVAILABLE_TO_COUNTRY_FIELD_ERROR
              : null
          }
          id="delivery-block-countries"
          label={COUNTRY_SELECT_LABEL}
          labelHidden
          onChange={handleCountryChange}
          options={groupedCountries}
          value={selectedCountry}
        />
        {showTimezoneMessage.current && (
          <div css={styles.timezoneMessageWrapper}>
            <IconInfoCircular height={16} width={16} />
            <p css={styles.timezoneMessage}>{TIMEZONE_MESSAGE}</p>
          </div>
        )}
      </header>
      {renderDeliveryOptions()}
      {photoUploadRequired && (
        <>
          <hr />
          <PhotoPersonalisationMessage />
        </>
      )}
    </section>
  );
};
